import { UseFormReturn, FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date_picker";
import { ExistingClientFormData } from "./schemas";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/api/utils";

interface ExistingClientFormProps {
  form: UseFormReturn<ExistingClientFormData>;
  errors: FieldErrors<ExistingClientFormData>;
  isPreview?: boolean;
}

export function ExistingClientForm({ form, errors, isPreview = false }: ExistingClientFormProps) {
  const { register, setValue, watch } = form;

  const exitDateValue = watch("exit_date");

  const exitDateAsDate = exitDateValue
    ? moment.tz(exitDateValue, CommonDestinationTimezone).toDate()
    : undefined;

  const handleExitDateChange = (date: Date | undefined) => {
    if (date) {
      const isoString = moment.tz(
        moment(date).format("YYYY-MM-DD"),
        "YYYY-MM-DD",
        CommonDestinationTimezone
      ).toISOString();
      setValue("exit_date", isoString, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue("exit_date", "", { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <div className="space-y-4">
      {/* Client Name */}
      <div className="space-y-1">
        <Label htmlFor="customer_name">Client Name *</Label>
        <Input
          id="customer_name"
          {...register("customer_name")}
          placeholder="Enter client name"
          disabled={isPreview}
          className="w-full"
        />
        {errors.customer_name && (
          <p className="text-sm text-destructive">{errors.customer_name.message}</p>
        )}
      </div>

      {/* Exit Date */}
      <div className="space-y-1">
        <Label htmlFor="exit_date">Exit Date *</Label>
        <DatePicker
          value={exitDateAsDate}
          onChange={handleExitDateChange}
          placeholder="Select exit date"
          buttonClassName="w-full"
        />
        {errors.exit_date && (
          <p className="text-sm text-destructive">{errors.exit_date.message}</p>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Enter any additional notes"
          rows={3}
          disabled={isPreview}
          className="w-full resize-none"
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      {/* Hidden Schema Version Fields */}
      <input type="hidden" {...register("schema_version")} value="v1" />
      <input type="hidden" {...register("schema_type")} value="exiting_client_form" />
    </div>
  );
}
